import { Button, Callout, Card, FormLayout, Heading, LoadingSpinner, Paragraph, SubmitButton } from '@expressable/syntax-ds';
import { ContentContainer } from 'components/ContentContainer';
import { HeadingCombo } from 'domains/signupDirect/SyntaxComps/HeadingCombo';
import { useDisplayTimezone } from 'domains/timezone/useDisplayTimezone';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DATE_LONG_DAY_MONTH_YEAR_TIME, TIME_ONLY_WITH_ZONE, TIME_ZONE_ONLY } from 'utils/date';
import { RescheduleDirectCalendarPicker } from './blocks/RescheduleDirectCalendarPicker';
import { UseEvaluationCalendar } from 'domains/calendars/v3/useEvaluationCalendar';
import { useFormContext, useWatch } from 'react-hook-form';
import { RescheduleDirectFormData } from './schema';
import { UseEvaluationCalendarResult } from 'domains/signupDirect/misc/useSignupEvaluationCalendar';
import { Reschedule, PartialRescheduleClientInformation } from 'domains/direct-reschedule/types';
import { NoAppointmentsDialog } from './blocks/NoAppointmentsDialog';
import config from 'config';
import { ContentfulTherapistSummary } from 'components/ContentfulTherapistCard/ContentfulTherapistSummary';
import { toStateAbbrev, StateName } from 'utils/states';

type UiProps = {
  rescheduleInfo: Reschedule;
  clientInfoData: PartialRescheduleClientInformation;
  isLoading: boolean;
  onConfirm: () => void;
  availability: UseEvaluationCalendarResult;
};

export const RescheduleDirectUi: FC<UiProps> = ({
  rescheduleInfo,
  clientInfoData,
  isLoading,
  onConfirm,
  availability,
}) => {
  const { t } = useTranslation();
  const { format } = useDisplayTimezone();
  const { setValue, handleSubmit, clearErrors, control } = useFormContext<RescheduleDirectFormData>();
  const [isNoAppointmentsDialogOpen, setIsNoAppointmentsDialogOpen] = useState(false);
  
  // Watch the selected time slot to enable/disable the Continue button
  const selectedTimeSlot = useWatch({ control, name: 'timeSlot' });

  const onChangeTimeSlot = (timeSlot: Date | undefined) => {
    if (timeSlot) {
      setValue('timeSlot', timeSlot);
      clearErrors('timeSlot');
    }
  };

  const onChangeDay = (day: number) => {
    availability.onChangeDay(day);
    clearErrors();
    setValue('timeSlot', undefined);
    setValue('day', undefined);
    if (availability.currentAvailability) {
      setValue('day', availability.currentAvailability.day);
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  // No availability - show full page message
  if (!availability || !availability?.allTimeSlots?.some(day => day.timeSlotCount > 0)) {
    return (
      <ContentContainer variant="full">
        <ContentContainer variant="centered-md" className="my-40">
          <HeadingCombo>
            <Heading noDefaultMargins level="h1">
              {t(
                'rescheduleDirect.init.noAvailability',
                "We're sorry, there are no available appointments at this time.",
              )}
            </Heading>
            <Paragraph variant="secondary">
              {t(
                'rescheduleDirect.init.noAvailabilitySubtitle',
                'Please text your therapist or contact our support team for scheduling assistance.',
              )}
            </Paragraph>
          </HeadingCombo>

          <Button size="lg" className="mt-64" as="a" href={`mailto:${config.get('supportEmail')}`}>
            {t('reschedule.cantFindAppointment.contactSupport', 'Email us for support')}
          </Button>
        </ContentContainer>
      </ContentContainer>
    );
  }

  return (
    <ContentContainer variant="full">
      <ContentContainer variant="centered-md" className="items-stretch">
          <HeadingCombo>
            <Heading noDefaultMargins level="h1" className="mt-40">
              {t('rescheduleDirect.init.title', {
                date: format(new Date(rescheduleInfo.originalAcuityAppointment.datetime), DATE_LONG_DAY_MONTH_YEAR_TIME),
                time: format(new Date(rescheduleInfo.originalAcuityAppointment.datetime), TIME_ONLY_WITH_ZONE),
              })}
            </Heading>
            <Paragraph variant="secondary">
              {clientInfoData?.allowMultipleTreatingTherapists
                ? t('rescheduleDirect.init.subTitleMulti', {
                    timezone: format(new Date(rescheduleInfo.originalAcuityAppointment.datetime), TIME_ZONE_ONLY),
                  })
                : t('rescheduleDirect.init.subTitleSingle', {
                    timezone: format(new Date(rescheduleInfo.originalAcuityAppointment.datetime), TIME_ZONE_ONLY),
                  })}
            </Paragraph>
          </HeadingCombo>

        <Callout type="info" className="my-64">
          {t('rescheduleDirect.init.sameDayWarning')}
        </Callout>

        <FormLayout variant="default" onSubmit={handleSubmit(onConfirm)}>
          {/* In multi-therapist mode: show calendar picker as before */}
          {clientInfoData?.allowMultipleTreatingTherapists ? (
            <RescheduleDirectCalendarPicker
              availability={availability as unknown as UseEvaluationCalendar}
              onChangeTimeSlot={onChangeTimeSlot}
              onChangeDay={onChangeDay}
              upcomingAppointmentDates={clientInfoData?.upcomingAppointmentDates || []}
              originalAppointmentDate={rescheduleInfo.originalAcuityAppointment.datetime}
              careTeamPrimaryTherapistEmail={clientInfoData?.careTeam?.primary?.therapistEmail ?? ''}
              careTeamPrimaryTherapistName={clientInfoData?.careTeam?.primary?.therapistName}
              allowMultipleTreatingTherapists={clientInfoData?.allowMultipleTreatingTherapists ?? true}
            />
          ) : (
            <>
              {/* In single-therapist mode: show only date picker first (no checkbox, no time slots) */}
              <div className="w-full">
                <RescheduleDirectCalendarPicker
                  availability={availability as unknown as UseEvaluationCalendar}
                  onChangeTimeSlot={onChangeTimeSlot}
                  onChangeDay={onChangeDay}
                  upcomingAppointmentDates={clientInfoData?.upcomingAppointmentDates || []}
                  originalAppointmentDate={rescheduleInfo.originalAcuityAppointment.datetime}
                  careTeamPrimaryTherapistEmail={clientInfoData?.careTeam?.primary?.therapistEmail ?? ''}
                  careTeamPrimaryTherapistName={clientInfoData?.careTeam?.primary?.therapistName}
                  allowMultipleTreatingTherapists={false}
                  showOnlyDayPicker={true}
                />
              </div>

              {/* Section heading for available times */}
              {availability.currentAvailability && (
                <Heading level="h4" noDefaultMargins  className="md:mb-[-24px]">
                  {t('rescheduleDirect.init.availableTimesOn', {
                    date: format(availability.currentAvailability.day, 'EEEE, MMMM d'),
                  })}
                </Heading>
              )}

              {/* Show therapist card with times inside for single therapist mode */}
              {clientInfoData?.careTeam?.primary && (
                <Card className="mb-40 w-full">
                  <ContentfulTherapistSummary
                    therapistName={clientInfoData.careTeam.primary.therapistName}
                    stateCode={toStateAbbrev(clientInfoData?.state as unknown as StateName) ?? undefined}
                    clinicalAreas={clientInfoData?.detectedAreas ?? []}
                    insuranceName={clientInfoData?.insuranceNameContentful}
                    shouldShowProfile={true}
                  />
                  <div className="mt-24">
                    <RescheduleDirectCalendarPicker
                      availability={availability as unknown as UseEvaluationCalendar}
                      onChangeTimeSlot={onChangeTimeSlot}
                      onChangeDay={onChangeDay}
                      upcomingAppointmentDates={clientInfoData?.upcomingAppointmentDates || []}
                      originalAppointmentDate={rescheduleInfo.originalAcuityAppointment.datetime}
                      careTeamPrimaryTherapistEmail={clientInfoData?.careTeam?.primary?.therapistEmail ?? ''}
                      careTeamPrimaryTherapistName={clientInfoData?.careTeam?.primary?.therapistName}
                      allowMultipleTreatingTherapists={false}
                      showOnlyTimeSlots={true}
                    />
                  </div>
                </Card>
              )}
            </>
          )}

          <FormLayout.CTA>
              <SubmitButton type="submit" disabled={!selectedTimeSlot}>
                {t('rescheduleDirect.init.submit', 'Continue')}
              </SubmitButton>
              <Button variant="link" size="md" type="button" onClick={() => setIsNoAppointmentsDialogOpen(true)}>
                {t('rescheduleDirect.init.noAppointmentsWork', 'No appointments work for me')}
              </Button>
          </FormLayout.CTA>
        </FormLayout>

        <NoAppointmentsDialog
          isOpen={isNoAppointmentsDialogOpen}
          onDismiss={() => setIsNoAppointmentsDialogOpen(false)}
        />
      </ContentContainer>
    </ContentContainer>
  );
};
